'use client'

import { useAuth } from '@clerk/react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { inlineEditProps } from '@/components/settings/inline-edit'
import type { SettingsSectionBodyProps } from '@/components/settings/sections'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  REVEAL_ON_HOVER,
  REVEAL_ON_HOVER_SCRIM,
} from '@/components/ui/reveal-on-hover'
import { useUserSettings } from '@/components/user-settings-context'
import {
  getPresetLayout,
  getStoredLayout,
  isValidLayout,
  normalizeRetiredLayout,
  PRESET_LAYOUTS,
  storeActiveLayoutId,
  storeLayout,
} from '@/lib/layout-presets'
import { cn } from '@/lib/utils'
import { LayoutThumbnail } from './layout-thumbnail'

const MAX_CUSTOM_LAYOUTS = 3

/** Lifts the name/delete dialogs above Clerk's UserProfile modal. */
const ABOVE_CLERK_MODAL = 'z-[100000]'

interface LayoutsSettingsProps extends SettingsSectionBodyProps {
  /** Set when mounted inside Clerk's UserProfile modal, which sits above z-50. */
  aboveClerkModal?: boolean
}

export function LayoutsSettings({
  onDone,
  aboveClerkModal = false,
}: LayoutsSettingsProps = {}) {
  const { settings, updateSettings } = useUserSettings()
  const { userId } = useAuth()
  const dialogLayer = aboveClerkModal ? ABOVE_CLERK_MODAL : undefined
  // Clerk's modal closes on any Escape that reaches the document. Radix only
  // marks its Escape handled, so stop it here or Escape in a layout dialog
  // closes Clerk's whole modal too.
  const keepEscapeFromClerk = aboveClerkModal
    ? (event: KeyboardEvent) => event.stopPropagation()
    : undefined
  const layouts = settings.layouts
  const [saveName, setSaveName] = useState('')
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [renameTarget, setRenameTarget] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const applyLayout = useCallback(
    (layoutId: string) => {
      if (!userId) return
      const isSignedIn = true // userId is set, so the user is signed in

      // Look up the layout JSON
      let layoutJson = getPresetLayout(layoutId, isSignedIn)
      if (!layoutJson) {
        const custom = layouts.custom.find((c) => c.id === layoutId)
        if (custom) {
          layoutJson = normalizeRetiredLayout(
            custom.model as unknown as import('flexlayout-react').IJsonModel,
          )
        }
      }

      if (!layoutJson) return

      // Write to localStorage synchronously BEFORE updating settings
      storeLayout(userId, layoutJson)
      storeActiveLayoutId(userId, layoutId)
      // Update active layout ID (triggers editor useEffect)
      updateSettings({ layouts: { ...layouts, activeLayoutId: layoutId } })
      // Close the surrounding modal so the user sees the applied layout
      onDone?.()
      toast('Layout applied')
    },
    [userId, layouts, updateSettings, onDone],
  )

  const handleSaveCustom = useCallback(() => {
    if (!userId) return

    const currentLayout = getStoredLayout(userId)
    if (!currentLayout) {
      toast('No current layout found to save')
      return
    }

    if (layouts.custom.length >= MAX_CUSTOM_LAYOUTS) {
      toast('Maximum 3 custom layouts. Delete one to save a new layout.')
      return
    }

    setShowSaveDialog(true)
  }, [userId, layouts])

  const confirmSave = useCallback(() => {
    if (!userId || !saveName.trim()) return

    const currentLayout = getStoredLayout(userId)
    if (!currentLayout) return

    if (!isValidLayout(currentLayout)) {
      toast('Current layout is corrupted and cannot be saved')
      return
    }

    const newId = crypto.randomUUID()
    const newCustom = [
      ...layouts.custom,
      {
        id: newId,
        name: saveName.trim(),
        model: currentLayout as unknown as Record<string, unknown>,
      },
    ]

    updateSettings({
      layouts: { activeLayoutId: newId, custom: newCustom },
    })
    storeLayout(userId, currentLayout)
    storeActiveLayoutId(userId, newId)
    setShowSaveDialog(false)
    setSaveName('')
    toast(`Layout "${saveName.trim()}" saved`)
  }, [userId, saveName, layouts, updateSettings])

  const handleDelete = useCallback(
    (id: string) => {
      if (!userId) return
      const newCustom = layouts.custom.filter((c) => c.id !== id)
      const newActiveId =
        layouts.activeLayoutId === id ? 'default' : layouts.activeLayoutId

      // If deleting the active layout, revert to default
      if (layouts.activeLayoutId === id) {
        const defaultLayout = getPresetLayout('default', true)
        if (defaultLayout) {
          storeLayout(userId, defaultLayout)
          storeActiveLayoutId(userId, 'default')
        }
      }

      updateSettings({
        layouts: { activeLayoutId: newActiveId, custom: newCustom },
      })
      setDeleteTarget(null)
    },
    [userId, layouts, updateSettings],
  )

  const handleRename = useCallback(
    (id: string) => {
      if (!renameValue.trim()) {
        // Treat empty rename as a cancel: exit rename mode and clear the value
        setRenameTarget(null)
        setRenameValue('')
        return
      }
      const newCustom = layouts.custom.map((c) =>
        c.id === id ? { ...c, name: renameValue.trim() } : c,
      )
      updateSettings({ layouts: { ...layouts, custom: newCustom } })
      setRenameTarget(null)
      setRenameValue('')
    },
    [renameValue, layouts, updateSettings],
  )

  return (
    <div>
      {/* Preset layouts */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">
          Presets
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {PRESET_LAYOUTS.map((preset) => {
            const isActive =
              (layouts.activeLayoutId === 'interview'
                ? 'default'
                : layouts.activeLayoutId) === preset.id
            const presetModel = preset.buildLayout(true)
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyLayout(preset.id)}
                className={`group relative rounded-lg border-2 p-2 transition-colors hover:border-primary/60 ${
                  isActive ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <LayoutThumbnail model={presetModel} />
                <span className="mt-1.5 block text-xs font-medium">
                  {preset.name}
                </span>
                <span
                  className={cn(
                    'absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 text-sm font-medium text-white',
                    REVEAL_ON_HOVER_SCRIM,
                  )}
                >
                  Apply
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Custom layouts */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">
          Custom ({layouts.custom.length}/{MAX_CUSTOM_LAYOUTS})
        </h3>
        {layouts.custom.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-3">
            {layouts.custom.map((custom) => {
              const isActive = layouts.activeLayoutId === custom.id
              return (
                <div
                  key={custom.id}
                  className={`group relative rounded-lg border-2 p-2 transition-colors ${
                    isActive ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => applyLayout(custom.id)}
                    className="w-full"
                  >
                    <LayoutThumbnail
                      model={normalizeRetiredLayout(
                        custom.model as unknown as import('flexlayout-react').IJsonModel,
                      )}
                    />
                    {renameTarget !== custom.id && (
                      <span className="mt-1.5 block text-xs font-medium">
                        {custom.name}
                      </span>
                    )}
                    {/* Kept out of the tree while renaming: the scrim covers
                        the whole card, inline input included. */}
                    {renameTarget !== custom.id && (
                      <span
                        className={cn(
                          'absolute inset-0 flex items-center justify-center rounded-lg bg-black/40 text-sm font-medium text-white',
                          REVEAL_ON_HOVER_SCRIM,
                        )}
                      >
                        Apply
                      </span>
                    )}
                  </button>
                  {renameTarget === custom.id && (
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(custom.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(custom.id)
                        if (e.key === 'Escape') {
                          // Cancel the rename only; Clerk's modal would close
                          // on an Escape that reaches the document.
                          e.stopPropagation()
                          setRenameTarget(null)
                        }
                      }}
                      {...inlineEditProps}
                      className="mt-1.5 block w-full text-xs border border-border rounded px-1 py-0.5 bg-background"
                      // biome-ignore lint/a11y/noAutofocus: Input must focus for inline rename editing
                      autoFocus
                    />
                  )}
                  {/* Edit/Delete controls */}
                  <div
                    className={cn(
                      'absolute top-1 right-1 flex gap-0.5',
                      REVEAL_ON_HOVER,
                    )}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setRenameTarget(custom.id)
                        setRenameValue(custom.name)
                      }}
                      className="rounded p-1 hover:bg-muted"
                      aria-label={`Rename ${custom.name}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(custom.id)
                      }}
                      className="rounded p-1 hover:bg-muted text-destructive"
                      aria-label={`Delete ${custom.name}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          onClick={handleSaveCustom}
          disabled={layouts.custom.length >= MAX_CUSTOM_LAYOUTS}
          title={
            layouts.custom.length >= MAX_CUSTOM_LAYOUTS
              ? 'Maximum 3 custom layouts. Delete one to save a new layout.'
              : undefined
          }
        >
          <Plus className="h-4 w-4" />
          Save current layout
        </Button>
      </div>

      <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <AlertDialogContent
          className={cn('sm:max-w-80', dialogLayer)}
          overlayClassName={dialogLayer}
          onEscapeKeyDown={keepEscapeFromClerk}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Name your layout</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              Save the current editor layout under a name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            type="text"
            aria-label="Layout name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && saveName.trim()) confirmSave()
            }}
            placeholder="e.g. My Custom Layout"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            // biome-ignore lint/a11y/noAutofocus: Dialog input must focus for better UX
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </AlertDialogCancel>
            <Button size="sm" onClick={confirmSave} disabled={!saveName.trim()}>
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent
          className={cn('sm:max-w-80', dialogLayer)}
          overlayClassName={dialogLayer}
          onEscapeKeyDown={keepEscapeFromClerk}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete layout?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &ldquo;
              {layouts.custom.find((c) => c.id === deleteTarget)?.name}
              &rdquo;? This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </AlertDialogCancel>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                if (deleteTarget) handleDelete(deleteTarget)
              }}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
